// Yahoo Finance symbol mapping for the keyless /api proxy.
// Used for both live quotes (live engine) and historical OHLC (charts).

// Explicit per-symbol overrides (crypto, commodities, bonds, EU/IN tickers).
const ASSET_EXPLICIT: Record<string, string> = {
  BTC: 'BTC-USD', ETH: 'ETH-USD', SOL: 'SOL-USD',
  GOLD: 'GC=F', WTI: 'CL=F', US10Y: '^TNX',
  MC: 'MC.PA', ASML: 'ASML.AS', RELI: 'RELIANCE.NS',
};

/** Yahoo chart symbol for one of our ASSETS, or null if no real series exists. */
export function assetYahoo(a: { sym: string; market: string }): string | null {
  if (ASSET_EXPLICIT[a.sym]) return ASSET_EXPLICIT[a.sym];
  switch (a.market) {
    case 'SET': return a.sym + '.BK';
    case 'JP': return a.sym + '.T';
    case 'HK': return a.sym + '.HK';
    case 'KR': return a.sym + '.KS';
    case 'US': return a.sym; // US stocks + ETFs use the bare ticker
    default: return null;    // mai, Thai gov bond, etc. → keep simulated
  }
}

// Country (DIV_STOCKS `co`) → Yahoo exchange suffix for the dividend screener.
const DIV_SUFFIX: Record<string, string> = { TH: '.BK', US: '', UK: '.L', JP: '.T', HK: '.HK' };
// EU has no single suffix — the exchange varies by company — so the handful of
// European names we carry are mapped one by one. Without this they resolve to
// nothing, which also costs them their logo.
const DIV_EU: Record<string, string> = { ALV: 'ALV.DE', BAS: 'BAS.DE' };
/** Yahoo quote symbol for a dividend-screener row (co defaults to TH). null when unmappable. */
export function divYahoo(sym: string, co?: string): string | null {
  const c = co || 'TH';
  if (c === 'EU') return DIV_EU[sym] || null;
  const suf = DIV_SUFFIX[c];
  return suf === undefined ? null : sym + suf;
}

/** Yahoo chart symbol for a dashboard INDEX, or null. */
const INDEX_YAHOO: Record<string, string> = {
  SET: '^SET.BK', MAI: '^MAI.BK', SPX: '^GSPC', NDX: '^NDX', DJI: '^DJI',
  NI225: '^N225', UKX: '^FTSE', USDTHB: 'THB=X',
  // Index CFDs under their MT5 broker names, mapped to the cash index Yahoo
  // quotes — all six verified against /api/yquotes before being added.
  AUS200: '^AXJO', EU50: '^STOXX50E', FRA40: '^FCHI', GER40: '^GDAXI',
  CN50: 'XIN9.FGI', HK50: '^HSI',
  GOLD: 'GC=F', // same ticker as the GOLD asset — one batched quote serves both
};
export function indexYahoo(sym: string): string | null { return INDEX_YAHOO[sym] || null; }

// (MT5 timeframe → Yahoo range/interval mapping lives server-side in
//  server/providers.mjs `TF_YAHOO`, since some timeframes need aggregation.)

// Non-US stock symbols the live engine polls for quotes (subset of assetYahoo).
export const INTL_QUOTE_YAHOO: Record<string, string> = {
  PTT: 'PTT.BK', AOT: 'AOT.BK', CPALL: 'CPALL.BK', DELTA: 'DELTA.BK', KBANK: 'KBANK.BK',
  '7203': '7203.T', '6758': '6758.T',
  '0700': '0700.HK', '9988': '9988.HK',
  MC: 'MC.PA', ASML: 'ASML.AS',
  RELI: 'RELIANCE.NS',
  '005930': '005930.KS',
};
